/**
 * MechaGuard - AudioSystem (程序化音效与音乐)
 *
 * 本项目不含任何音频文件 —— 所有音效与背景音乐都用 Web Audio API
 * 实时合成。好处是零资源体积、无加载等待、可无限变体；
 * 代价是音色偏「电子」，但这恰好符合科幻机甲主题。
 *
 * 三个必须处理好的现实问题
 * ------------------------
 * 1. **自动播放策略**：浏览器要求 AudioContext 必须在用户手势里创建/恢复。
 *    所以构造函数里不建 context，由 main.js 在首次 pointerdown 时调 unlock()。
 * 2. **音效过载**：一屏十几座激光塔每 0.12 秒各打一发，直接播会糊成噪音。
 *    这里做了两层限流：单音效节流 + 全局 100ms 内最多 6 个音。
 * 3. **无音频环境**：Node（无头校验）或浏览器不支持时，全部方法安全空转，
 *    绝不抛异常。
 */

import { readJson, writeJson } from './Storage.js';
import { MusicSequencer } from './MusicSequencer.js';

const PREF_KEY = 'mechguard.audio.v1';

/** 全局限流：窗口长度（秒） */
const RATE_WINDOW = 0.1;
/** 全局限流：窗口内最多允许多少个音效 */
const RATE_MAX = 6;

/* ============================================================
 * 音效音色表
 *
 * 每项：
 *   throttle  同名音效的最小间隔（秒），避免连射时糊掉
 *   build(a)  用 a.tone() / a.noise() 描述这个音
 * ============================================================ */
const SFX = {
  /* ---------- 武器 ---------- */
  laser: {
    throttle: 0.07,
    build(a) {
      a.tone({ freq: 1700, freqTo: 620, type: 'sawtooth', dur: 0.07, gain: 0.09 });
      a.tone({ freq: 2500, freqTo: 900, type: 'square', dur: 0.05, gain: 0.03 });
    },
  },

  plasma: {
    throttle: 0.12,
    build(a) {
      a.tone({ freq: 320, freqTo: 90, type: 'sawtooth', dur: 0.22, gain: 0.11 });
      a.noise({ dur: 0.16, gain: 0.08, type: 'bandpass', freq: 900, freqTo: 300, q: 1.2 });
    },
  },

  tesla: {
    throttle: 0.10,
    build(a) {
      a.noise({ dur: 0.14, gain: 0.10, type: 'bandpass', freq: 3200, freqTo: 1400, q: 5 });
      a.tone({ freq: 1400, freqTo: 500, type: 'square', dur: 0.09, gain: 0.045 });
    },
  },

  cryo: {
    throttle: 0.30,
    build(a) {
      a.tone({ freq: 500, freqTo: 1500, type: 'sine', dur: 0.30, gain: 0.055 });
      a.noise({ dur: 0.28, gain: 0.045, type: 'highpass', freq: 4000, q: 0.8 });
    },
  },

  railgun: {
    throttle: 0.20,
    build(a) {
      a.noise({ dur: 0.10, gain: 0.15, type: 'highpass', freq: 1800, q: 0.7 });
      a.tone({ freq: 240, freqTo: 70, type: 'sawtooth', dur: 0.30, gain: 0.13 });
    },
  },

  missile: {
    throttle: 0.25,
    build(a) {
      // 发射呼啸：上滑音 + 气流噪声，区别于等离子/轨道炮
      a.tone({ freq: 220, freqTo: 680, type: 'sawtooth', dur: 0.30, gain: 0.10 });
      a.noise({ dur: 0.26, gain: 0.10, type: 'bandpass', freq: 1600, freqTo: 400, q: 1.0 });
    },
  },

  /* ---------- 命中 / 死亡 ---------- */
  hit: {
    throttle: 0.05,
    build(a) {
      a.noise({ dur: 0.05, gain: 0.06, type: 'bandpass', freq: 2200, q: 1.5 });
    },
  },

  enemyDeath: {
    throttle: 0.05,
    build(a) {
      a.tone({ freq: 420, freqTo: 90, type: 'square', dur: 0.20, gain: 0.09 });
      a.noise({ dur: 0.18, gain: 0.07, type: 'bandpass', freq: 1500, freqTo: 400, q: 1 });
    },
  },

  explosion: {
    throttle: 0.08,
    build(a) {
      a.tone({ freq: 180, freqTo: 40, type: 'sine', dur: 0.55, gain: 0.26 });
      a.noise({ dur: 0.50, gain: 0.20, type: 'lowpass', freq: 1200, freqTo: 200, q: 0.6 });
    },
  },

  /* ---------- 建造 ---------- */
  build: {
    throttle: 0,
    build(a) {
      a.tone({ freq: 520, type: 'square', dur: 0.07, gain: 0.09 });
      a.tone({ freq: 780, type: 'square', dur: 0.09, gain: 0.075, start: 0.06 });
    },
  },

  upgrade: {
    throttle: 0,
    build(a) {
      [660, 880, 1180].forEach((f, i) => {
        a.tone({ freq: f, type: 'triangle', dur: 0.12, gain: 0.095, start: i * 0.06 });
      });
    },
  },

  sell: {
    throttle: 0,
    build(a) {
      [700, 520, 380].forEach((f, i) => {
        a.tone({ freq: f, type: 'triangle', dur: 0.10, gain: 0.075, start: i * 0.05 });
      });
    },
  },

  /* ---------- UI ---------- */
  click: {
    throttle: 0.03,
    build(a) {
      a.tone({ freq: 900, type: 'square', dur: 0.035, gain: 0.045 });
    },
  },

  error: {
    throttle: 0.15,
    build(a) {
      a.tone({ freq: 160, freqTo: 120, type: 'square', dur: 0.18, gain: 0.09 });
    },
  },

  /* ---------- 战斗事件 ---------- */
  leak: {
    throttle: 0.20,
    build(a) {
      a.tone({ freq: 400, freqTo: 180, type: 'sawtooth', dur: 0.35, gain: 0.15 });
      a.noise({ dur: 0.30, gain: 0.11, type: 'lowpass', freq: 800, q: 0.8 });
    },
  },

  waveStart: {
    throttle: 0.50,
    build(a) {
      a.tone({ freq: 220, freqTo: 660, type: 'triangle', dur: 0.50, gain: 0.11 });
    },
  },

  waveClear: {
    throttle: 0.50,
    build(a) {
      [523, 659, 784].forEach((f, i) => {
        a.tone({ freq: f, type: 'triangle', dur: 0.22, gain: 0.095, start: i * 0.09 });
      });
    },
  },

  /* ---------- 技能 ---------- */
  skillEmp: {
    throttle: 0.30,
    build(a) {
      a.tone({ freq: 1800, freqTo: 200, type: 'sawtooth', dur: 0.60, gain: 0.17 });
      a.noise({ dur: 0.50, gain: 0.13, type: 'bandpass', freq: 2600, freqTo: 400, q: 2 });
    },
  },

  skillShield: {
    throttle: 0.30,
    build(a) {
      [440, 660, 880].forEach((f, i) => {
        a.tone({ freq: f, type: 'sine', dur: 0.50, gain: 0.095, start: i * 0.05 });
      });
    },
  },

  skillOrbital: {
    throttle: 0.30,
    build(a) {
      a.tone({ freq: 900, freqTo: 60, type: 'sawtooth', dur: 0.70, gain: 0.19 });
      a.noise({ dur: 0.80, gain: 0.19, type: 'lowpass', freq: 2000, freqTo: 150, q: 0.7 });
    },
  },

  /* ---------- BOSS ---------- */
  bossPhase: {
    throttle: 0.50,
    build(a) {
      a.tone({ freq: 300, freqTo: 80, type: 'sawtooth', dur: 0.80, gain: 0.19 });
      a.tone({ freq: 150, freqTo: 50, type: 'square', dur: 0.90, gain: 0.13 });
      a.noise({ dur: 0.70, gain: 0.13, type: 'lowpass', freq: 1600, freqTo: 300, q: 0.8 });
    },
  },

  /* ---------- 结算 ---------- */
  victory: {
    throttle: 0,
    build(a) {
      [523, 659, 784, 1046].forEach((f, i) => {
        a.tone({ freq: f, type: 'triangle', dur: 0.40, gain: 0.13, start: i * 0.14 });
      });
    },
  },

  defeat: {
    throttle: 0,
    build(a) {
      [392, 330, 262, 196].forEach((f, i) => {
        a.tone({ freq: f, type: 'triangle', dur: 0.50, gain: 0.13, start: i * 0.18 });
      });
    },
  },

  /* ---------- 成就 ---------- */
  achievement: {
    throttle: 0,
    build(a) {
      [784, 988, 1319].forEach((f, i) => {
        a.tone({ freq: f, type: 'sine', dur: 0.35, gain: 0.11, start: i * 0.08 });
      });
    },
  },
};

export { SFX };

/** 供 UI / 测试查询某个音效是否存在 */
export function hasSfx(name) {
  return Object.prototype.hasOwnProperty.call(SFX, name);
}

export class AudioSystem {
  /**
   * @param {object} [opts]
   * @param {{getItem:Function,setItem:Function}|null} [opts.storage]
   */
  constructor({ storage = null } = {}) {
    this.storage = storage;

    this.ctx = null;
    this.available = false;
    this.unlockAttempted = false;

    this.enabled = true;
    this.musicEnabled = true;
    this.volume = 0.7;

    this._master = null;
    this._sfxGain = null;
    this._musicGain = null;
    /** @type {MusicSequencer|null} */
    this._music = null;
    this._noiseBuf = null;

    /** @type {number[]} 最近的音效时间戳（全局限流） */
    this._recent = [];
    /** @type {Map<string, number>} 单音效上次播放时间 */
    this._lastPlayed = new Map();

    /** @type {'menu'|'combat'|'boss'|'off'} */
    this._musicMode = 'menu';

    this._loadPrefs();
  }

  /* ===================================================
   * 偏好持久化
   * =================================================== */

  _loadPrefs() {
    const prefs = readJson(this.storage, PREF_KEY, null);
    if (!prefs || typeof prefs !== 'object') return;

    if (typeof prefs.enabled === 'boolean') this.enabled = prefs.enabled;
    if (typeof prefs.musicEnabled === 'boolean') this.musicEnabled = prefs.musicEnabled;
    if (typeof prefs.volume === 'number' && prefs.volume >= 0 && prefs.volume <= 1) {
      this.volume = prefs.volume;
    }
  }

  _savePrefs() {
    writeJson(this.storage, PREF_KEY, {
      enabled: this.enabled,
      musicEnabled: this.musicEnabled,
      volume: this.volume,
    });
  }

  /* ===================================================
   * 生命周期
   * =================================================== */

  /**
   * 创建 / 恢复 AudioContext。
   * **必须在用户手势（pointerdown / click）里调用**，否则浏览器会拒绝启动音频。
   * 重复调用是安全的。
   * @returns {boolean} 音频是否可用
   */
  unlock() {
    this.unlockAttempted = true;

    // 已经有 context：只需确保它在跑
    if (this.ctx) {
      if (this.ctx.state === 'suspended') {
        try { this.ctx.resume(); } catch { /* 忽略 */ }
      }
      return this.available;
    }

    if (typeof window === 'undefined') return false;

    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) {
      this.available = false;
      return false;
    }

    try {
      this.ctx = new Ctor();

      // 音量图：sfx / music -> master -> 输出
      this._master = this.ctx.createGain();
      this._master.gain.value = this.volume;
      this._master.connect(this.ctx.destination);

      this._sfxGain = this.ctx.createGain();
      this._sfxGain.gain.value = 0.9;
      this._sfxGain.connect(this._master);

      this._musicGain = this.ctx.createGain();
      this._musicGain.gain.value = 0.45;
      this._musicGain.connect(this._master);

      this._music = new MusicSequencer({
        ctx: this.ctx,
        destination: this._musicGain,
      });

      if (this.ctx.state === 'suspended') {
        try { this.ctx.resume(); } catch { /* 忽略 */ }
      }

      this.available = true;
      this._applyMusicMode();
      return true;
    } catch {
      this.ctx = null;
      this._master = null;
      this._sfxGain = null;
      this._musicGain = null;
      this._music = null;
      this.available = false;
      return false;
    }
  }

  /** 释放资源（一般不需要，页面关闭会自动回收） */
  dispose() {
    if (this._music) this._music.dispose();
    this._music = null;

    if (this.ctx) {
      try { this.ctx.close(); } catch { /* 忽略 */ }
    }

    this.ctx = null;
    this.available = false;
    this._master = null;
    this._sfxGain = null;
    this._musicGain = null;
  }

  /* ===================================================
   * 开关
   * =================================================== */

  /** 总开关（音效 + 音乐） */
  setEnabled(on) {
    this.enabled = !!on;
    this._applyMusicMode();
    this._applyVolume();
    this._savePrefs();
  }

  toggle() {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  /** 只控背景音乐 */
  setMusicEnabled(on) {
    this.musicEnabled = !!on;
    this._applyMusicMode();
    this._savePrefs();
  }

  /** 0~1 */
  setVolume(v) {
    this.volume = Math.min(1, Math.max(0, Number(v) || 0));
    this._applyVolume();
    this._savePrefs();
  }

  _applyVolume() {
    if (!this._master) return;
    try {
      this._master.gain.value = this.enabled ? this.volume : 0;
    } catch { /* 忽略 */ }
  }

  /**
   * 切换背景音乐强度
   * @param {'menu'|'combat'|'boss'|'off'} mode
   */
  setMusicMode(mode) {
    this._musicMode = mode;
    this._applyMusicMode();
  }

  _applyMusicMode() {
    if (!this._music) return;

    const mode = (this.enabled && this.musicEnabled)
      ? (this._musicMode || 'menu')
      : 'off';

    this._music.setIntensity(mode);
  }

  /* ===================================================
   * 播放音效
   * =================================================== */

  /**
   * @param {string} name SFX 表里的键
   * @returns {boolean} 是否真的发了声（被限流或音频不可用时为 false）
   */
  play(name) {
    if (!this.enabled || !this.available || !this.ctx) return false;

    const patch = SFX[name];
    if (!patch) return false;

    // 浏览器可能在切后台后挂起 context
    if (this.ctx.state === 'suspended') {
      try { this.ctx.resume(); } catch { /* 忽略 */ }
    }

    const now = this.ctx.currentTime;

    // ---- 单音效节流 ----
    const throttle = patch.throttle || 0;
    if (throttle > 0) {
      const last = this._lastPlayed.get(name);
      if (last !== undefined && now - last < throttle) return false;
    }

    // ---- 全局限流：避免混战时糊成一片噪音 ----
    let keep = 0;
    for (let i = 0; i < this._recent.length; i++) {
      if (now - this._recent[i] < RATE_WINDOW) this._recent[keep++] = this._recent[i];
    }
    this._recent.length = keep;
    if (this._recent.length >= RATE_MAX) return false;

    this._lastPlayed.set(name, now);
    this._recent.push(now);

    try {
      patch.build(this);
      return true;
    } catch {
      return false;
    }
  }

  /* ===================================================
   * 合成原语（供 SFX 表使用）
   * =================================================== */

  /**
   * 一个振荡器音
   * @param {object} o
   * @param {number} o.freq 起始频率
   * @param {number} [o.freqTo] 结束频率（做滑音）
   * @param {OscillatorType} [o.type]
   * @param {number} [o.start] 相对现在的延迟（秒）
   * @param {number} [o.dur]
   * @param {number} [o.gain]
   * @param {number} [o.attack]
   */
  tone({
    freq,
    freqTo = null,
    type = 'sine',
    start = 0,
    dur = 0.2,
    gain = 0.15,
    attack = 0.005,
  }) {
    if (!this.ctx || !this._sfxGain) return;

    const t0 = this.ctx.currentTime + start;
    const f0 = Math.max(20, freq);

    const osc = this.ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t0);

    if (freqTo != null) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, freqTo), t0 + dur);
    }

    const g = this.ctx.createGain();
    // exponentialRamp 不能碰 0，用 0.0001 当静音底
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    osc.connect(g);
    g.connect(this._sfxGain);

    osc.start(t0);
    osc.stop(t0 + dur + 0.03);
  }

  /**
   * 一段噪声（用来做爆炸、电流、踩镲这类音色）
   * @param {object} o
   * @param {number} [o.start]
   * @param {number} [o.dur]
   * @param {number} [o.gain]
   * @param {BiquadFilterType} [o.type]
   * @param {number} [o.freq]
   * @param {number} [o.freqTo]
   * @param {number} [o.q]
   */
  noise({
    start = 0,
    dur = 0.2,
    gain = 0.1,
    type = 'bandpass',
    freq = 1200,
    freqTo = null,
    q = 1,
  }) {
    if (!this.ctx || !this._sfxGain) return;

    const t0 = this.ctx.currentTime + start;

    const src = this.ctx.createBufferSource();
    src.buffer = this._noiseBuffer();
    src.loop = true;

    const filter = this.ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.setValueAtTime(Math.max(20, freq), t0);
    if (freqTo != null) {
      filter.frequency.exponentialRampToValueAtTime(Math.max(20, freqTo), t0 + dur);
    }
    filter.Q.value = q;

    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    src.connect(filter);
    filter.connect(g);
    g.connect(this._sfxGain);

    src.start(t0);
    src.stop(t0 + dur + 0.02);
  }

  /** 1 秒白噪声缓冲，全程复用 */
  _noiseBuffer() {
    if (this._noiseBuf) return this._noiseBuf;

    const length = Math.max(1, Math.floor(this.ctx.sampleRate * 1));
    const buf = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buf.getChannelData(0);

    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;

    this._noiseBuf = buf;
    return buf;
  }
}
